/**
 * Paleta única dos fluxos biométricos (Identy e FaceTec).
 *
 * Regra de cor definida na proposta de UX BB:
 *   - azul     -> orientação normal (oval, faixa de feedback, progresso)
 *   - amarelo  -> ajuste necessário (oval fora do enquadramento)
 *   - vermelho -> apenas bloqueio ou falha terminal
 *
 * Estes valores são o DEFAULT. Quando a API do cliente enviar as chaves
 * equivalentes, elas prevalecem — o default só é usado no que não vier.
 */
export const BIOMETRIC_THEME_DEFAULTS = {
  surface: '#ffffff',
  ovalNormal: '#00409e',
  ovalAdjust: '#ffef00',
  feedbackBackground: '#00409e',
  feedbackText: '#ffffff',
  textPrimary: '#111214',
  progressTrack: '#dbe7ff',
  progressFill: '#00409e',
  buttonBackground: '#00409e',
  buttonText: '#ffffff',
  buttonHighlight: '#0922ae',
  buttonDisabled: '#ebf2ff',
  // Precisa contrastar com buttonDisabled: o rótulo do botão desabilitado usa
  // esta cor, e herdar o branco de buttonText o tornava ilegível.
  buttonTextDisabled: '#3d5a8a',
  danger: '#d32f2f'
}

/**
 * Chaves aceitas na configuração do cliente (API), em ordem de precedência.
 * A primeira chave presente e não vazia vence; caso nenhuma venha, cai no default.
 */
const CLIENT_KEYS_BY_TOKEN = {
  surface: ['biometric-surface-color'],
  ovalNormal: ['biometric-oval-color'],
  ovalAdjust: ['biometric-oval-adjust-color'],
  feedbackBackground: ['biometric-feedback-background', 'biometric-oval-color'],
  feedbackText: ['biometric-feedback-text-color'],
  textPrimary: ['biometric-text-color'],
  progressTrack: ['biometric-progress-track-color'],
  progressFill: ['biometric-progress-color', 'biometric-oval-color'],
  buttonBackground: ['biometric-button-color', 'background-button'],
  buttonText: ['biometric-button-text-color', 'text-button-color'],
  buttonHighlight: ['biometric-button-highlight-color'],
  buttonDisabled: ['biometric-button-disabled-color'],
  buttonTextDisabled: ['biometric-button-text-disabled-color'],
  danger: ['biometric-danger-color']
}

const isUsableColor = (value) => typeof value === 'string' && value.trim() !== ''

/**
 * Resolve a paleta biométrica a partir da configuração do cliente.
 * @param {object} [client] configuração vinda da API
 * @returns {typeof BIOMETRIC_THEME_DEFAULTS}
 */
export function resolveBiometricTheme(client) {
  const config = client || {}

  return Object.keys(BIOMETRIC_THEME_DEFAULTS).reduce((theme, token) => {
    const candidate = (CLIENT_KEYS_BY_TOKEN[token] || [])
      .map((key) => config[key])
      .find(isUsableColor)

    theme[token] = isUsableColor(candidate) ? candidate.trim() : BIOMETRIC_THEME_DEFAULTS[token]
    return theme
  }, {})
}

/**
 * Publica a paleta como CSS custom properties, para que o CSS de overlay
 * (diálogos do SDK Identy e telas de loading) use a mesma fonte de verdade.
 * @param {object} [client] configuração vinda da API
 */
export function applyBiometricThemeVariables(client) {
  if (typeof document === 'undefined') {
    return BIOMETRIC_THEME_DEFAULTS
  }

  const theme = resolveBiometricTheme(client)
  const root = document.documentElement.style

  root.setProperty('--biometric-surface', theme.surface)
  root.setProperty('--biometric-oval', theme.ovalNormal)
  root.setProperty('--biometric-oval-adjust', theme.ovalAdjust)
  root.setProperty('--biometric-feedback-background', theme.feedbackBackground)
  root.setProperty('--biometric-feedback-text', theme.feedbackText)
  root.setProperty('--biometric-text', theme.textPrimary)
  root.setProperty('--biometric-progress-track', theme.progressTrack)
  root.setProperty('--biometric-progress-fill', theme.progressFill)
  root.setProperty('--biometric-danger', theme.danger)

  return theme
}

/** Pilha tipográfica de último recurso, igual à que o portal usa por padrão. */
const FONTE_PADRAO = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

/**
 * Resolve a família tipográfica dos fluxos biométricos.
 *
 * O SDK da FaceTec fixa 'Source Sans Pro' em sete grupos de customização, o
 * que fazia a captura alternar de tipografia em relação ao resto do portal.
 * Aqui a ordem é: chave dedicada da API, depois a fonte de runtime do cliente
 * (--font-family-text, publicada por setLayoutPortal), depois o que o body
 * estiver realmente usando, e só então o fallback.
 *
 * @param {object} [client] configuração vinda da API
 * @returns {string} valor para font-family
 */
export function resolveBiometricFont(client) {
  const explicita = (client || {})['biometric-font-family']
  if (isUsableColor(explicita)) {
    return explicita.trim()
  }

  if (typeof document === 'undefined') {
    return FONTE_PADRAO
  }

  const daApi = getComputedStyle(document.documentElement)
    .getPropertyValue('--font-family-text')
    .trim()
  if (daApi) {
    return `${daApi}, ${FONTE_PADRAO}`
  }

  const doCorpo = document.body && getComputedStyle(document.body).fontFamily
  return (doCorpo && doCorpo.trim()) || FONTE_PADRAO
}
